import { ctaVariants, ImageTextCard } from '@greystar/designsystem';
import { getLinkIcon, useAnalytics, useFormattedAddress } from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { IconName, ResponsiveImage } from '@greystar/ui';
import { CardProps } from '@greystar/ui/src/component-props';
import {
  Link as JssLink,
  Text as JssText,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';

interface ExtendedCardProps extends CardProps {
  sectionHeading?: string;
}

export const Card = (props: ExtendedCardProps): JSX.Element => {
  const { sectionHeading } = props;
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing ?? false;
  const analytics = useAnalytics();
  const context = useSitecoreContext();
  const property: EventProperty = context?.sitecoreContext?.property ?? {};
  const location = property?.location ?? {};
  const shouldHaveMaxWidth = !props.isCarousel;

  const {
    ctaVariant,
    iconList,
    iconPosition,
    imageBorderColor,
    imageBorderType,
    imageAspectRatio,
    imageBorderRounded,
  } = props.rendering.params ?? {};

  // type inference for cta variant
  type CtaVariantKey = keyof typeof ctaVariants;
  const rawKey = ctaVariant as CtaVariantKey;
  const ctaVariantType = ctaVariants[rawKey] ?? ctaVariants.secondary;

  // type inference + fallback for cta icon position
  let position: 'left' | 'right' = 'left';
  if (iconPosition) {
    position = iconPosition.toLowerCase() === 'left' ? 'left' : 'right';
  }

  const ctaIcon = iconList ? iconList.replace(/\s+/g, '') : undefined;

  const property_location = useFormattedAddress(location);

  const { cardTitle, cardDescription, cardImage, link } = props.fields || {};

  const ctaLink = {
    label: link?.value?.text ?? 'Link Text Not Set',
    href: link?.value?.href ?? '/',
    variant: ctaVariantType,
    icon: { name: ctaIcon as IconName, position },
  };

  const handleCardClick = () => {
    const eventData = {
      event: 'explore_more',
      action_type: 'explore more',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      module_name: sectionHeading ?? '',
      card_title: cardTitle?.value ?? '',
      inpage_location: 'middle',
      click_text: link?.value?.text ?? 'Link Text Not Set',
      click_type: 'internal',
      destination_url: link?.value?.href ?? '',
    };

    analytics.trackEvent('explore_more', eventData);
  };

  const headingStyles = props?.params?.headingStyles as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <ImageTextCard
      withMaxWidth={shouldHaveMaxWidth}
      withPadding={true}
      title={<JssText field={cardTitle} />}
      bodyText={<JssText field={cardDescription} />}
      headerAs={headingStyles}
    >
      <ImageTextCard.Image
        aspectRatio={imageAspectRatio ? imageAspectRatio.replace('-', ':') : '3:4'}
        borderColour={imageBorderColor}
        borderType={imageBorderType}
        borderRounded={imageBorderRounded === '1'}
      >
        <ResponsiveImage
          image={cardImage}
          fill={true}
          sizes="(max-width: 768px) 95vw, (max-width: 1180px) 45vw, (max-width: 1440px) 33vw, 25vw"
        />
      </ImageTextCard.Image>
      {link?.value && (
        <ImageTextCard.Link
          aria-label={cardTitle?.value ? 'Read more about ' + cardTitle.value : 'Read more'}
          isEditable={isEditing}
          href={ctaLink?.href}
          variant={ctaLink?.variant}
          label={ctaLink?.label}
          icon={getLinkIcon({ iconName: ctaLink?.icon?.name, variant: ctaLink?.variant })}
          onClick={handleCardClick}
        >
          <JssLink field={link} />
        </ImageTextCard.Link>
      )}
    </ImageTextCard>
  );
};

export default withDatasourceCheck()<ExtendedCardProps>(Card);
